import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { ButtonHTMLAttributes } from 'react';
import { space, SpaceProps, variant } from 'styled-system';

import { UseType } from '@/components/blocks/Button/types';
import { Size } from '@/styles/types';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: Extract<Size, 's' | 'm'>;
  use?: UseType;
  icon?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  full?: boolean;
  onClick?: () => void;
}

const StyledButton = styled('button', {
  shouldForwardProp: (propName: string) => !['icon', 'full'].includes(propName),
})<Props & SpaceProps>(
  space,
  ({ theme }) =>
    css({
      minHeight: '42px',
      position: 'relative',
      cursor: 'pointer',
      border: 'none',
      outline: 0,
      fontWeight: theme.fontWeights.bold,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      borderRadius: theme.radii.m,
      '&:focus': {
        boxShadow: theme.shadows.default,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        opacity: theme.opacities.disabled,
        pointerEvents: 'none',
      },
    }),
  ({ theme }) =>
    variant({
      prop: 'size',
      variants: {
        s: css({
          padding: `${theme.spaces.s} ${theme.spaces.l}`,
          fontSize: theme.fontSizes.s,
          gap: theme.spaces.s,
          borderRadius: theme.radii.s,
        }),
        m: css({
          padding: `${theme.spaces.m} ${theme.spaces.xs}`,
          fontSize: theme.fontSizes.m,
          gap: theme.spaces.s,
        }),
      },
    }),
  ({ theme }) =>
    variant({
      prop: 'use',
      variants: {
        primary: css({
          color: theme.colors.text.inverse,
          background: theme.colors.background.secondary,
          '&:hover': {
            background: theme.colors.background.current,
          },
        }),
        secondary: css({
          color: theme.colors.text.default,
          border: theme.borders.default,
          background: theme.colors.background.default,
          '&:hover': {
            background: theme.colors.background.weak,
          },
        }),
        danger: css({
          color: theme.colors.text.inverse,
          background: theme.colors.fill.error,
        }),
      },
    }),
  ({ theme, icon }) =>
    !icon && {
      [`svg:first-of-type`]: {
        marginRight: theme.spaces.s,
      },
    },
  ({ full }) =>
    full && {
      width: '100%',
    }
);

export const Button = ({
  size = 'm',
  use = 'primary',
  full = false,
  children,
  ...props
}: Props) => {
  return (
    <StyledButton size={size} use={use} full={full} {...props}>
      {children}
    </StyledButton>
  );
};
